import React, { useEffect, useState } from "react"

import { GET, PATCH, POST } from 'helpers/api'

import { validateFactStep, validateLeaveStep, hasErrors, buildActualAmountsPayload, buildCashTransferPayload, parseShiftCloseDetails, actualToFact, CASH_ROWS, roundAmount } from './shiftCloseCalc'
import ShiftClosePanel from './ShiftClosePanel'
import './shiftClose.css'

const EMPTY_FACT = { cashUZS: '', cashUSD: '', terminal: '' }
const EMPTY_LEAVE = { cashUZS: '', cashUSD: '' }

// 2-step shift close: fact amounts -> cash left in the cashbox / safe
// `shift` is the cash-shifts list item; closed when it is null
function ShiftCloseFlow({ shift, onHide, onFinish }) {
	// expected amounts come from the backend, the modals open only after they are loaded
	const [details, setDetails] = useState(null)
	const show = !!shift && !!details

	const [step, setStep] = useState(1)
	const [fact, setFact] = useState(EMPTY_FACT)
	const [leave, setLeave] = useState(EMPTY_LEAVE)
	const [comment, setComment] = useState('')
	const [factErrors, setFactErrors] = useState({})

	const { expected, actual, currencyIds, paymentTypeIds } = details || parseShiftCloseDetails(null)

	const leaveErrors = validateLeaveStep(leave, fact)

	function handleFactChange(key, value) {
		setFact(prev => ({ ...prev, [key]: value }))
		setFactErrors(prev => {
			if (!prev[key]) return prev
			const next = { ...prev }
			delete next[key]
			return next
		})
	}

	function handleFactSubmit() {
		const errors = validateFactStep(fact)
		setFactErrors(errors)
		if (!hasErrors(errors)) setStep(2)
	}

	async function handleFinish() {
		if (hasErrors(leaveErrors)) return

		// fact amounts are saved first, the cash transfer goes only after that succeeds
		const updated = await PATCH(
			`/services/web/api/cash-shift/${shift.shiftId}/actual-amounts`,
			{ closeRequestId: shift.closeRequestId, amounts: buildActualAmountsPayload(fact, currencyIds, paymentTypeIds) },
			{ loader: true }
		)
		if (!updated) return

		const response = await POST(
			`/services/web/api/shift-close/${shift.closeRequestId}/cash-transfer`,
			buildCashTransferPayload(leave, currencyIds),
			{ loader: true }
		)
		if (response) onFinish(response.data)
	}

	async function getDetails(shiftId) {
		const response = await GET(`/services/web/api/cash-shift/${shiftId}/closing-details`, {}, { loader: true })
		if (response && !response.isAxiosError) {
			setDetails(parseShiftCloseDetails(response))
		} else {
			onHide()
		}
	}

	useEffect(() => {
		setDetails(null)
		if (shift) getDetails(shift.shiftId)
	}, [shift?.shiftId]) // eslint-disable-line react-hooks/exhaustive-deps

	// every opening starts from a clean state, fact is prefilled with backend actual amounts
	useEffect(() => {
		if (!show) return
		setStep(1)
		setFact(actualToFact(actual))
		setLeave(EMPTY_LEAVE)
		setComment('')
		setFactErrors({})
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	// "→|" button: fact = expected for every row
	function fillExpected() {
		setFact(actualToFact(expected))
		setFactErrors({})
	}

	// step 2 header buttons: "cashbox" = all cash stays, "safe" = all cash goes to the safe
	function fillLeave(mode) {
		const next = {}
		CASH_ROWS.forEach(row => {
			next[row.key] = mode === 'cashbox' ? String(roundAmount(fact[row.key], row.currency)) : '0'
		})
		setLeave(next)
	}

	return (
		<ShiftClosePanel
			show={show}
			step={step}
			shift={shift}
			expected={expected}
			fact={fact}
			leave={leave}
			comment={comment}
			factErrors={factErrors}
			leaveErrors={leaveErrors}
			onFactChange={handleFactChange}
			onFillExpected={fillExpected}
			onLeaveChange={(key, value) => setLeave(prev => ({ ...prev, [key]: value }))}
			onLeaveFill={fillLeave}
			onCommentChange={setComment}
			onNext={handleFactSubmit}
			onBack={() => setStep(1)}
			onCancel={onHide}
			onFinish={handleFinish}
		/>
	)
}

export default ShiftCloseFlow
